import { z } from 'zod';
import { DEMO_ROLE_CODES } from './demo';
import { InviteMail, InviteMessage } from './invite-mail';
import { LicenseStatus } from './licensing';
import { PLATFORM_ROLES, PlatformEntryState } from './platform-console';
export const ExternalProfileSchema = z.enum([
  'supplier',
  'subcontractor',
  'customer',
  'designer',
  'inspector',
]);

/* ───────────────────────── Общие ───────────────────────── */

export const Uuid = z.uuid();

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const OkSchema = z.object({ ok: z.literal(true) });

const Password = z
  .string()
  .min(10, 'Пароль не короче 10 символов')
  .max(200)
  .refine((v) => /[A-Za-zА-Яа-яЁё]/.test(v) && /\d/.test(v), 'Пароль должен содержать буквы и цифры');

const Email = z
  .email('Неверный адрес почты')
  .max(254)
  .transform((v) => v.trim().toLowerCase());

const PersonName = z.string().trim().min(2, 'Укажите имя и фамилию').max(200);

/* ───────────────────────── Вход ───────────────────────── */

export const LoginInput = z.object({
  email: Email,
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof LoginInput>;

export const SetupInput = z.object({
  token: z.string().min(16).max(200),
  /** Название компании заказчика; подставляется вместо нейтрального «Стартовая компания» (ПМ-12) */
  companyName: z.string().trim().min(2, 'Укажите название компании').max(200).optional(),
  fullName: PersonName,
  email: Email,
  position: z.string().trim().max(200).optional(),
  password: Password,
});
export type SetupInput = z.infer<typeof SetupInput>;

export const SetupStatus = z.object({
  needsSetup: z.boolean(),
  tenantName: z.string().nullable(),
});
export type SetupStatus = z.infer<typeof SetupStatus>;

export const InviteInfo = z.object({
  email: z.string(),
  fullName: z.string().nullable(),
  tenantName: z.string(),
  invitedBy: z.string().nullable(),
  expiresAt: z.string(),
  existingUser: z.boolean(),
});
export type InviteInfo = z.infer<typeof InviteInfo>;

export const InviteAcceptInput = z.object({
  fullName: PersonName,
  password: Password,
});
export type InviteAcceptInput = z.infer<typeof InviteAcceptInput>;

export const PasswordChangeInput = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: Password,
});
export type PasswordChangeInput = z.infer<typeof PasswordChangeInput>;

/* ── Восстановление пароля по почте (ДС-19) ── */

/** Сколько действует ссылка из письма */
export const PASSWORD_RESET_TTL_MINUTES = 60;

export const PasswordResetRequestInput = z.object({ email: Email });
export type PasswordResetRequestInput = z.infer<typeof PasswordResetRequestInput>;

/**
 * Ссылка из письма: 32 случайных байта в base64url. В письме она стоит после «#» (до сервера не доходит), а в API
 * передаётся в теле запроса — поэтому не попадает ни в журналы запросов, ни в заголовок Referer.
 */
const ResetToken = z
  .string()
  .regex(/^[A-Za-z0-9_-]{32,64}$/, 'Ссылка повреждена — откройте её из письма ещё раз');

export const PasswordResetCheckInput = z.object({ token: ResetToken });
export type PasswordResetCheckInput = z.infer<typeof PasswordResetCheckInput>;

/** Ссылка действует: для какой почты задаётся пароль и до какого времени */
export const PasswordResetInfo = z.object({
  email: z.string(),
  expiresAt: z.string(),
});
export type PasswordResetInfo = z.infer<typeof PasswordResetInfo>;

export const PasswordResetInput = z.object({ token: ResetToken, password: Password });
export type PasswordResetInput = z.infer<typeof PasswordResetInput>;

/** Пароль задан: почта — чтобы подставить её на странице входа */
export const PasswordResetDone = z.object({ ok: z.literal(true), email: z.string() });
export type PasswordResetDone = z.infer<typeof PasswordResetDone>;

/**
 * «Сбросить пароль» в «Сотрудниках»: ссылку отправляет администратор, человек может прочитать почту не сразу —
 * поэтому она действует дольше, чем ссылка, которую человек запросил сам
 */
export const PASSWORD_RESET_ADMIN_TTL_HOURS = 24;

/** Ссылка ушла: на какую почту и до какого времени действует (самой ссылки в ответе нет) */
export const MemberPasswordResetResult = z.object({ email: z.string(), expiresAt: z.string() });
export type MemberPasswordResetResult = z.infer<typeof MemberPasswordResetResult>;

/** Действующие права пользователя в текущей компании по областям. */
/**
 * Уровни доступа к деньгам (ДС-04): выдаются людям, а не ролям. Меняет только владелец портала.
 * none — без сумм; warehouse — суммы склада; acts — акты; acts_invoices — акты и счета; all — все деньги.
 */
export const MONEY_LEVELS = {
  none: 'Без денег',
  warehouse: 'Склад',
  acts: 'Акты',
  acts_invoices: 'Акты и счета',
  all: 'Все деньги',
} as const;
export type MoneyLevel = keyof typeof MONEY_LEVELS;
export const MoneyLevelSchema = z.enum(Object.keys(MONEY_LEVELS) as [MoneyLevel, ...MoneyLevel[]]);

export const AccessSchema = z.object({
  /** Точный список проектов внешнего участника: без наследования на дочерние проекты. */
  externalProjectIds: z.array(Uuid).optional(),
  tenant: z.array(z.string()),
  legalEntities: z.record(z.string(), z.array(z.string())),
  directions: z.record(z.string(), z.array(z.string())),
  projects: z.record(z.string(), z.array(z.string())),
  money: MoneyLevelSchema,
  /**
   * Уровень денег по областям (ДС-18): юрлицо, направление, проект (с подпроектами). Действует наибольший из
   * общего `money` и уровня области записи; без области записи считается только общий уровень.
   * Нет поля — уровней по областям нет.
   */
  moneyIn: z
    .object({
      legalEntities: z.record(z.string(), MoneyLevelSchema),
      directions: z.record(z.string(), MoneyLevelSchema),
      projects: z.record(z.string(), MoneyLevelSchema),
    })
    .optional(),
});
export type Access = z.infer<typeof AccessSchema>;
export type MoneyScopesMap = NonNullable<Access['moneyIn']>;

export const MeResponse = z.object({
  user: z.object({
    id: Uuid,
    email: z.string(),
    fullName: z.string(),
    position: z.string().nullable(),
    locale: z.string(),
    hasPasskey: z.boolean(),
    /** Владелец портала: кабинет компаний и лицензий (ЛЦ-05); данные компаний при этом не открываются */
    isPlatformAdmin: z.boolean(),
    /** Демо-пользователь (ГЛ-03): код роли демо; null — обычный пользователь */
    demoRole: z.enum(DEMO_ROLE_CODES).nullable(),
    /** Команда портала (ЛЦ-09): владелец или администратор портала; null — не в команде */
    platformRole: z.enum(PLATFORM_ROLES).nullable().optional(),
  }),
  tenant: z
    .object({
      id: Uuid,
      name: z.string(),
      slug: z.string(),
      isOwner: z.boolean(),
      /** Сотрудник компании (не внешний участник): видит внутренние разделы — карточку проекта, поручения */
      staff: z.boolean(),
      /** Демо-компания: внешние каналы выключены, данные каждую ночь возвращаются к исходным (ГЛ-02) */
      isDemo: z.boolean(),
      /** Контрагент внешнего участника (ВК-02): подрядчик и поставщик видят только своё */
      counterpartyId: Uuid.nullable(),
    })
    .nullable(),
  /** Состояние лицензии текущей компании: плашка, «только чтение», разделы тарифа (ЛЦ-03, ЛЦ-04) */
  license: LicenseStatus.nullable(),
  tenants: z.array(z.object({ id: Uuid, name: z.string() })),
  legalEntities: z.array(z.object({ id: Uuid, name: z.string(), shortName: z.string() })),
  roles: z.array(
    z.object({
      roleId: Uuid,
      code: z.string(),
      name: z.string(),
      scopeType: z.enum(['tenant', 'legal_entity', 'direction', 'project']),
      scopeId: Uuid.nullable(),
      scopeName: z.string().nullable(),
    }),
  ),
  access: AccessSchema,
  /**
   * Активные замещения (ДС-07): заместитель действует с правами этих людей;
   * в истории изменений его действия помечаются «за <ФИО>».
   */
  actingFor: z.array(z.object({ userId: Uuid, fullName: z.string() })),
  /**
   * «Глазами пользователя» (ДС-08): администратор смотрит портал как user выше — только просмотр.
   * byName — кто смотрит; until — когда режим закончится сам. null или нет поля — обычная работа.
   */
  viewAs: z
    .object({ byUserId: Uuid, byName: z.string(), userId: Uuid, fullName: z.string(), until: z.string() })
    .nullable()
    .optional(),
  /**
   * Вход команды портала в компанию (ЛЦ-11): человек не сотрудник этой компании, работает в ней по входу из
   * кабинета — плашка на каждой странице и «Выйти». null или нет поля — обычная работа.
   */
  platformEntry: PlatformEntryState.nullable().optional(),
});
export type MeResponse = z.infer<typeof MeResponse>;

/** Включить «глазами пользователя» (ДС-08) */
export const ViewAsStartInput = z.object({ userId: Uuid });
export type ViewAsStartInput = z.infer<typeof ViewAsStartInput>;

/** Журнал режима «глазами пользователя» (ДС-08) для администратора компании */
export const ViewAsLogEntry = z.object({
  id: Uuid,
  viewerName: z.string(),
  targetName: z.string(),
  startedAt: z.string(),
  until: z.string(),
  endedAt: z.string().nullable(),
  endReason: z.enum(['stop', 'replaced', 'expired', 'revoked']).nullable(),
});
export type ViewAsLogEntry = z.infer<typeof ViewAsLogEntry>;

/* ───────────────────────── Юрлица ───────────────────────── */

export const LegalEntity = z.object({
  id: Uuid,
  name: z.string(),
  shortName: z.string(),
  legalForm: z.string().nullable(),
  bin: z.string().nullable(),
  address: z.string().nullable(),
  directorName: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
});
export type LegalEntity = z.infer<typeof LegalEntity>;

export const LegalEntityInput = z.object({
  name: z.string().trim().min(2).max(300),
  shortName: z.string().trim().min(1).max(100),
  legalForm: z.string().trim().max(50).nullable().optional(),
  bin: z
    .string()
    .trim()
    .regex(/^\d{12}$/, 'БИН — 12 цифр')
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  address: z.string().trim().max(500).nullable().optional(),
  directorName: z.string().trim().max(200).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type LegalEntityInput = z.infer<typeof LegalEntityInput>;

/* ───────────────────────── Роли и пользователи ───────────────────────── */

export const ScopeTypeSchema = z.enum(['tenant', 'legal_entity', 'direction', 'project']);

export const Role = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  kind: z.enum(['internal', 'external']),
  isSystem: z.boolean(),
  permissions: z.array(z.string()),
  memberCount: z.number().int(),
});
export type Role = z.infer<typeof Role>;

export const RoleInput = z.object({
  name: z.string().trim().min(2).max(200),
  description: z.string().trim().max(1000).nullable().optional(),
  kind: z.enum(['internal', 'external']).optional(),
  permissions: z.array(z.string()).max(500),
});
export type RoleInput = z.infer<typeof RoleInput>;

export const AssignmentInput = z.object({
  roleId: Uuid,
  scopeType: ScopeTypeSchema,
  scopeId: Uuid.nullable(),
});
export type AssignmentInput = z.infer<typeof AssignmentInput>;

export const RoleAssignment = AssignmentInput.extend({
  id: Uuid,
  roleName: z.string(),
  scopeName: z.string().nullable(),
});
export type RoleAssignment = z.infer<typeof RoleAssignment>;

/** Уровень денег человека в одной области (ДС-18): проект с подпроектами, направление или юрлицо */
export const MoneyScopeInput = z.object({
  scopeType: z.enum(['legal_entity', 'direction', 'project']),
  scopeId: Uuid,
  moneyLevel: MoneyLevelSchema,
});
export type MoneyScopeInput = z.infer<typeof MoneyScopeInput>;

export const MoneyScope = MoneyScopeInput.extend({ scopeName: z.string().nullable() });
export type MoneyScope = z.infer<typeof MoneyScope>;

export const Member = z.object({
  externalProfile: ExternalProfileSchema.nullable(),
  externalProjectIds: z.array(Uuid),
  membershipId: Uuid,
  userId: Uuid,
  email: z.string(),
  fullName: z.string(),
  position: z.string().nullable(),
  status: z.enum(['active', 'disabled']),
  isOwner: z.boolean(),
  kind: z.enum(['staff', 'external']),
  moneyLevel: MoneyLevelSchema,
  /** Уровни денег по проектам, направлениям и юрлицам (ДС-18); видит только владелец компании, другим — [] */
  moneyScopes: z.array(MoneyScope),
  lastLoginAt: z.string().nullable(),
  roles: z.array(RoleAssignment),
  /** Контрагент внешнего участника (ВК-02) */
  counterpartyId: Uuid.nullable(),
  counterpartyName: z.string().nullable(),
});
export type Member = z.infer<typeof Member>;

export const MemberUpdateInput = z.object({
  externalProfile: ExternalProfileSchema.nullable().optional(),
  externalProjectIds: z.array(Uuid).max(100).optional(),
  position: z.string().trim().max(200).nullable().optional(),
  status: z.enum(['active', 'disabled']).optional(),
  kind: z.enum(['staff', 'external']).optional(),
  /** Меняет только владелец портала (ДС-04) */
  moneyLevel: MoneyLevelSchema.optional(),
  /** Уровни денег по областям — весь список целиком (ДС-18); меняет только владелец компании */
  moneyScopes: z.array(MoneyScopeInput).max(100).optional(),
  assignments: z.array(AssignmentInput).max(100).optional(),
  /** Контрагент внешнего участника (ВК-02); null — снять связь. У сотрудника компании не задаётся */
  counterpartyId: Uuid.nullable().optional(),
  /**
   * Обходной лист (СК-33, СК-27): отключение сотрудника, за которым числятся ТМЦ, инструмент или СИЗ,
   * отклоняется (409 с перечнем); с этим флагом — отключается с долгом, долг остаётся в учёте склада
   */
  forceDisable: z.boolean().optional(),
  /** Почему закрыт вход (карточка пользователя): видят администраторы компании */
  statusReason: z.string().trim().max(500).nullable().optional(),
  /** ФИО и телефон учётной записи: меняются, только если человек работает лишь в этой компании */
  fullName: z.string().trim().min(2).max(200).optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  /** Отвязать Telegram человека (только null): подключает Telegram сам человек кодом из профиля */
  telegramChatId: z.null().optional(),
});
export type MemberUpdateInput = z.infer<typeof MemberUpdateInput>;

/** Карточка пользователя в администрировании: всё о человеке в компании и что с ним можно сделать */
export const MemberCard = z.object({
  member: Member,
  phone: z.string().nullable(),
  /** ФИО, телефон и Telegram правятся только у человека, который работает лишь в этой компании */
  personalEditable: z.boolean(),
  personalLock: z.string().nullable(),
  telegram: z.object({ linked: z.boolean(), username: z.string().nullable() }),
  worker: z.object({ id: Uuid, fullName: z.string(), personnelNo: z.string().nullable() }).nullable(),
  /** Карточки работников без учётной записи (и своя) — для выбора */
  workerOptions: z.array(z.object({ id: Uuid, fullName: z.string(), personnelNo: z.string().nullable() })),
  login: z.object({
    hasPassword: z.boolean(),
    passkeys: z.number().int(),
    lastLoginAt: z.string().nullable(),
  }),
  /** Неотвеченное приглашение на ту же почту — его можно отправить заново */
  pendingInvite: z.object({ id: Uuid, expiresAt: z.string(), moneyLevel: MoneyLevelSchema }).nullable(),
  disabled: z
    .object({ reason: z.string().nullable(), at: z.string().nullable(), byName: z.string().nullable() })
    .nullable(),
  /** Почему нельзя убрать человека из компании; null — можно */
  removeLock: z.string().nullable(),
});
export type MemberCard = z.infer<typeof MemberCard>;

/** Письмо с доступом: куда ушло и отправлено ли на самом деле */
export const MemberAccessMailResult = z.object({ email: z.string(), reviewed: z.boolean() });
export type MemberAccessMailResult = z.infer<typeof MemberAccessMailResult>;

/** Что числится за сотрудником при отключении (обходной лист) */
export const MemberClearance = z.object({
  /** Позиций ТМЦ на руках (подтверждённых и ожидающих подтверждения) */
  items: z.number().int(),
  /** Единиц инструмента и оборудования на руках */
  tools: z.number().int(),
  /** Записей выданных СИЗ без возврата */
  ppe: z.number().int(),
  lines: z.array(z.string()),
});
export type MemberClearance = z.infer<typeof MemberClearance>;

export const InviteInput = z.object({
  externalProfile: ExternalProfileSchema.nullable().optional(),
  externalProjectIds: z.array(Uuid).max(100).optional(),
  email: Email,
  fullName: z.string().trim().max(200).optional(),
  position: z.string().trim().max(200).optional(),
  kind: z.enum(['staff', 'external']).default('staff'),
  /** Уровень денег; выше «без денег» выдаёт только владелец портала (ДС-04) */
  moneyLevel: MoneyLevelSchema.default('none'),
  /** Уровни денег по областям (ДС-18); выдаёт только владелец компании */
  moneyScopes: z.array(MoneyScopeInput).max(100).default([]),
  assignments: z.array(AssignmentInput).min(1, 'Назначьте хотя бы одну роль').max(100),
  /** Контрагент внешнего участника (ВК-02): с первого входа видит только своё */
  counterpartyId: Uuid.nullable().optional(),
});
export type InviteInput = z.infer<typeof InviteInput>;

export const Invite = z.object({
  id: Uuid,
  email: z.string(),
  fullName: z.string().nullable(),
  kind: z.enum(['staff', 'external']),
  moneyLevel: MoneyLevelSchema,
  invitedBy: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  acceptedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  /** Ссылка возвращается только при создании приглашения */
  link: z.string().optional(),
  /** Письмо с инструкцией «С чего начать» (БЗ-31); null — письмо не отправлялось */
  mail: InviteMail.nullable().optional(),
  /** Текст приглашения с инструкцией — только при создании и повторной отправке, как и ссылка */
  message: InviteMessage.optional(),
});
export type Invite = z.infer<typeof Invite>;

/* ───────────────────────── Замещение (ДС-07) ───────────────────────── */

/** Максимальный срок замещения и временного доступа — 120 дней */
export const DELEGATION_MAX_DAYS = 120;

export const DelegationInput = z.object({
  principalUserId: Uuid,
  delegateUserId: Uuid,
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  /** Выше «без денег» — только владелец компании; не выше уровня замещаемого */
  moneyLevel: MoneyLevelSchema.default('none'),
  note: z.string().trim().max(500).nullable().optional(),
});
export type DelegationInput = z.infer<typeof DelegationInput>;

export const Delegation = z.object({
  id: Uuid,
  principalUserId: Uuid,
  principalName: z.string(),
  delegateUserId: Uuid,
  delegateName: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  moneyLevel: MoneyLevelSchema,
  note: z.string().nullable(),
  revokedAt: z.string().nullable(),
  /** active — действует сейчас; scheduled — ещё не началось; ended — истекло; revoked — отозвано */
  status: z.enum(['active', 'scheduled', 'ended', 'revoked']),
  createdAt: z.string(),
});
export type Delegation = z.infer<typeof Delegation>;

/* ───────────────────────── История изменений ───────────────────────── */

export const HistoryChange = z.object({
  field: z.string(),
  before: z.unknown(),
  after: z.unknown(),
});

export const HistoryEntry = z.object({
  id: z.string(),
  at: z.string(),
  op: z.enum(['I', 'U', 'D']),
  table: z.string(),
  rowId: z.string().nullable(),
  actor: z.object({ id: Uuid, fullName: z.string() }).nullable(),
  changes: z.array(HistoryChange),
});
export type HistoryEntry = z.infer<typeof HistoryEntry>;

/* ───────────────────────── Файлы ───────────────────────── */

export const FileInfo = z.object({
  id: Uuid,
  name: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  createdAt: z.string(),
  uploadedBy: z.string().nullable(),
  /** Вид вложения, например документ комплекта к оплате (ФН-19) */
  kind: z.string().nullable().optional(),
  /** Проверка антивирусом (DEV-10): pending — проверяется, infected — в карантине, error — проверить не удалось */
  scanState: z.enum(['pending', 'clean', 'infected', 'error', 'not_scanned']).optional(),
});
export type FileInfo = z.infer<typeof FileInfo>;

export const FILE_SCAN_LABELS: Record<NonNullable<FileInfo['scanState']>, string> = {
  pending: 'Проверяется антивирусом',
  clean: 'Проверен антивирусом',
  infected: 'Заблокирован: найдена угроза',
  error: 'Проверка не удалась',
  not_scanned: 'Не проверялся',
};

/** Файл в карантине для администратора (DEV-10) */
export const QuarantinedFile = z.object({
  id: Uuid,
  name: z.string(),
  sizeBytes: z.number(),
  scanState: z.enum(['infected', 'error']),
  scanResult: z.string().nullable(),
  scannedAt: z.string().nullable(),
  createdAt: z.string(),
  uploadedBy: z.string().nullable(),
  attachedTo: z.number(),
});
export type QuarantinedFile = z.infer<typeof QuarantinedFile>;

export const FileScanStatus = z.object({
  enabled: z.boolean(),
  serviceUp: z.boolean().nullable(),
  counts: z.record(z.string(), z.number()),
});
export type FileScanStatus = z.infer<typeof FileScanStatus>;

/* ───────────────────────── Вход по Face ID, Touch ID, Windows Hello ───────────────────────── */

export const Passkey = z.object({
  id: Uuid,
  name: z.string(),
  /** singleDevice — только это устройство, multiDevice — синхронизируется через iCloud или Google */
  deviceType: z.string().nullable(),
  backedUp: z.boolean(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type Passkey = z.infer<typeof Passkey>;

export const PasskeyRenameInput = z.object({ name: z.string().trim().min(1).max(100) });
export type PasskeyRenameInput = z.infer<typeof PasskeyRenameInput>;
